<!--
CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
SPDX-License-Identifier: LicenseRef-Cercle-Interne

Fichier : apps/web/src/App.vue
Rôle : coque de l'application : en-tête fixe, tiroirs latéraux, zone centrale.
Référence : designsystem.md ; CIG-SPECIFICATION.md §5.
-->
<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue';
import { RouterView } from 'vue-router';
import { useSession } from './lib/store.ts';
import { useNavigation } from './lib/navigation.ts';
import { useGlissementDeBord, estEtroit } from './lib/glissement.ts';
import EnteteApplication from './shell/EnteteApplication.vue';
import BarreLaterale from './shell/BarreLaterale.vue';
import PoigneeLaterale from './shell/PoigneeLaterale.vue';
import CartoucheUtilisateur from './shell/CartoucheUtilisateur.vue';
import AvertissementAppareil from './AvertissementAppareil.vue';
import Rouet from './shell/Rouet.vue';
import Toasts from './shell/Toasts.vue';

const session = useSession();
const navigation = useNavigation();
const etroit = ref(false);
const lateraleOuverte = ref(true);

/**
 * Largeur de la barre latérale. Elle est réglée par l'utilisateur au
 * glissement et survit à la session : un réglage de confort qu'il faudrait
 * refaire à chaque ouverture n'en serait pas un.
 */
const CLE_LARGEUR = 'cig.laterale.largeur';
const largeurLaterale = ref(lireLargeur());

function lireLargeur(): number {
  const brut = Number(localStorage.getItem(CLE_LARGEUR));
  return Number.isFinite(brut) && brut >= 176 && brut <= 448 ? brut : 240;
}

function memoriserLargeur(): void {
  localStorage.setItem(CLE_LARGEUR, String(largeurLaterale.value));
}

function mesurer(): void {
  const avant = etroit.value;
  etroit.value = estEtroit();
  // La barre est déployée d'office sur grand écran, repliée sur mobile.
  if (avant !== etroit.value) lateraleOuverte.value = !etroit.value;
}

// Sur écran étroit, le tiroir s'ouvre au glissement depuis le bord gauche.
useGlissementDeBord({
  inactif: () => !etroit.value,
  onVersDroite: () => (lateraleOuverte.value = true),
  onVersGauche: () => (lateraleOuverte.value = false),
});

onMounted(() => {
  mesurer();
  window.addEventListener('resize', mesurer);
  void session.charger();
});
onUnmounted(() => window.removeEventListener('resize', mesurer));

/** Le voile n'existe que sur mobile : ailleurs, la barre est une colonne. */
const voileVisible = computed(() => etroit.value && lateraleOuverte.value);
</script>

<template>
  <AvertissementAppareil />

  <EnteteApplication
    :laterale-ouverte="lateraleOuverte"
    @basculer-laterale="lateraleOuverte = !lateraleOuverte"
  >
    <template #fin>
      <CartoucheUtilisateur v-if="session.profil" />
    </template>
  </EnteteApplication>

  <!-- Bandeau permanent d'impersonation : rien ne peut passer pour une action
       authentique de l'utilisateur (spécification §15.4.1). -->
  <div v-if="session.profil?.impersonatedBy" class="bandeau bandeau--impersonation" role="status">
    Vous agissez comme <strong>{{ session.profil.displayName ?? session.profil.sub }}</strong>,
    via {{ session.profil.impersonatedBy }}.
  </div>

  <div v-if="session.lectureSeule" class="bandeau bandeau--lecture-seule" role="status">
    Plateforme en lecture seule. Les modifications sont suspendues.
  </div>

  <div
    class="coque"
    :class="{ 'coque--bandeau': session.profil?.impersonatedBy || session.lectureSeule }"
    :style="{ '--cig-laterale': `${largeurLaterale}px` }"
  >
    <Transition name="voile">
      <div v-if="voileVisible" class="voile" @click="lateraleOuverte = false" />
    </Transition>

    <aside
      class="colonne"
      :class="{ 'colonne--ouverte': lateraleOuverte, 'colonne--tiroir': etroit }"
      :inert="etroit && !lateraleOuverte"
    >
      <BarreLaterale v-if="session.profil" @naviguer="etroit && (lateraleOuverte = false)" />

      <!-- La poignée n'a de sens que sur une barre ouverte : repliée, il n'y
           a rien à redimensionner. -->
      <PoigneeLaterale
        v-if="session.profil && lateraleOuverte"
        v-model:largeur="largeurLaterale"
        @termine="memoriserLargeur"
      />
    </aside>

    <!--
      Zone centrale. Pendant une navigation, la vue en place demeure, estompée
      et inerte : la saisie est neutralisée, un rouet marque l'attente, et la
      nouvelle vue ne paraît qu'une fois prête.
    -->
    <main class="centre" :class="{ 'centre--attente': navigation.enCours }">
      <div v-if="navigation.enCours" class="voile-navigation" aria-hidden="true">
        <Rouet :taille="30" libelle="Chargement de la vue" />
      </div>

      <p v-if="session.chargement" class="attente cig-discret">Chargement…</p>

      <div v-else-if="session.erreur" class="accueil">
        <div class="cig-carte">
          <h1>Connexion impossible</h1>
          <p class="cig-discret">{{ session.erreur }}</p>
          <p><a class="cig-bouton" href="/auth/login">Se connecter</a></p>
        </div>
      </div>

      <div v-else class="vue" :inert="navigation.enCours">
        <RouterView />
      </div>
    </main>
  </div>

  <!-- Avis passagers. Ils vivent hors de la coque : ils commentent l'action en
       cours, quelle que soit la vue, et survivent à son remplacement. -->
  <Toasts />
</template>

<style scoped>
.bandeau {
  position: fixed;
  top: var(--cig-entete);
  left: 0;
  right: 0;
  z-index: 39;
  padding: var(--cig-espace-1) var(--cig-espace-3);
  font-family: var(--cig-sans);
  font-size: 0.8rem;
  text-align: center;
}
.bandeau--impersonation { background: var(--cig-alerte); color: #fff; font-weight: 600; }
.bandeau--lecture-seule { background: var(--cig-gris-clair); color: var(--cig-alerte); font-weight: 600; }

.coque {
  position: fixed;
  inset: var(--cig-entete) 0 0 0;
  display: flex;
  min-height: 0;
}
.coque--bandeau { top: calc(var(--cig-entete) + 1.9rem); }

.colonne {
  position: relative;
  width: var(--cig-laterale);
  flex: none;
  transition: margin-left var(--cig-duree) var(--cig-elan);
  z-index: 25;
  background: var(--cig-contraste);
}
.colonne:not(.colonne--ouverte) { margin-left: calc(var(--cig-laterale) * -1); }

/* Sur écran étroit, la barre devient un tiroir posé au-dessus du contenu. */
.colonne--tiroir {
  position: fixed;
  inset: var(--cig-entete) auto 0 0;
  /* Le tiroir est posé sur un voile : il prend l'élévation des modales. */
  box-shadow: var(--cig-ombre-modale);
}

.voile { position: fixed; inset: 0; z-index: 24; background: rgb(12 14 16 / 30%); }
.voile-enter-active, .voile-leave-active { transition: opacity var(--cig-duree-courte) ease; }
.voile-enter-from, .voile-leave-to { opacity: 0; }

.centre {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/*
 * Attente de navigation. La vue sortante s'estompe sans disparaître : un écran
 * vide donnerait à croire que la page a planté, là où l'application charge.
 */
.centre { position: relative; }
.vue {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  transition: opacity var(--cig-duree-courte) ease;
}
.centre--attente .vue { opacity: 0.35; }

.voile-navigation {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: grid;
  place-items: center;
  /* Le voile intercepte la saisie : cliquer deux fois sur un lien ne doit pas
     lancer deux navigations concurrentes. */
  cursor: progress;
}

.attente { padding: var(--cig-espace-4); }
.accueil { padding: var(--cig-espace-4); max-width: 34rem; margin: 0 auto; width: 100%; }

@media (prefers-reduced-motion: reduce) {
  .colonne, .vue { transition: none; }
}
</style>

<style>
/*
 * Gabarits de vue.
 *
 * Les sélecteurs ne s'ancrent pas à la zone centrale : la vue servie est
 * emballée dans le conteneur de transition de navigation, et non posée
 * directement dans `.centre`. Un `.centre > …` ne désignerait donc rien, et la
 * vue se retrouverait sans marge ni défilement.
 */

/*
 * Les vues ordinaires conservent une mise en page en carte centrée : elles
 * portent un contenu de lecture, que le blanc détache du plan en retrait.
 */
.vue-standard {
  flex: 1;
  min-height: 0;
  overflow: auto;
  /* La gouttière est réservée en permanence : sans elle, la colonne se décale
     latéralement dès qu'une liste devient assez longue pour défiler. */
  scrollbar-gutter: stable;
  padding: var(--cig-espace-4);
  width: 100%;
}
.vue-standard > * { max-width: 88rem; margin: 0 auto; }

/*
 * Vues de travail : collections.
 *
 * Ce sont des espaces où l'on agit sur des listes, comme l'explorateur, et non
 * des documents que l'on lit. Elles occupent donc toute la zone centrale, sur
 * fond blanc et sans ombre : une carte posée au milieu d'un grand écran
 * gaspillerait la place là où la place sert précisément à voir plus de lignes.
 *
 * Le rythme d'espacement est celui de l'explorateur, aux mêmes valeurs, pour
 * qu'une vue à l'autre rien ne se décale.
 */
.vue-pleine {
  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  width: 100%;
  padding: var(--cig-espace-3);
  background: var(--cig-conteneur);
}
.vue-pleine > * { max-width: none; margin: 0; }

/*
 * La page d'une vue de travail ne porte aucun décor : ni fond propre, ni
 * ombre, ni arrondi. Le conteneur l'a déjà mise sur blanc, et rien ne se
 * trouve derrière elle qu'il faudrait laisser paraître.
 */
.vue-pleine > .page {
  background: transparent;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
}

/*
 * Vues de travail à barre : revues d'accès, circuits de validation.
 *
 * Elles reprennent le gabarit de l'explorateur, dont elles partagent le geste :
 * une barre de tête qui nomme la vue et porte ses commandes, puis un corps qui
 * défile seul. La barre et le corps portent exactement les marges de la barre
 * et de la grille de l'explorateur.
 */
.vue-travail {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--cig-conteneur);
}

.vue-travail > .barre-travail {
  display: flex;
  align-items: center;
  gap: var(--cig-espace-2);
  flex: none;
  flex-wrap: wrap;
  padding: var(--cig-espace-2) var(--cig-espace-3);
  border-bottom: 1px solid var(--cig-gris-clair);
}

.vue-travail > .corps-travail {
  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  padding: var(--cig-espace-3);
}

/*
 * Corps fait de cartouches : il prend le gris de fond, comme le volet de
 * propriétés. Les cartouches sont blancs, et un blanc sur blanc ne se
 * détacherait pas. Un corps qui porte un tableau ou un graphe reste en
 * revanche sur le blanc du conteneur, comme la grille de l'explorateur.
 */
.vue-travail > .corps-travail--plan { background: var(--cig-fond); }
</style>
